import type { GridApi, GridApiCommon } from "@mui/x-data-grid";
import type { RefObject } from "react";
import { useEffect } from "react";

type ExtractGridInitialState<T extends GridApi> =
  T extends GridApiCommon<infer Row, infer InitialState>
    ? Row extends unknown
      ? InitialState
      : InitialState
    : never;

// Reference: https://mui.com/x/react-data-grid/state/#save-and-restore-the-state-from-external-storage
const useSaveGridSnapshot = <T extends GridApi>(
  apiRef: RefObject<T | null>,
  key: string | undefined
) => {
  // Dynamically derive the GridInitialState type from the apiRef type
  type GridInitialStateFromApi = ExtractGridInitialState<T>;

  // We deviate from the reference, which reads localStorage in a useLayoutEffect
  // and renders a placeholder until it resolves. That defends against SSR (no
  // localStorage on the server; render-time reads cause hydration mismatch) and
  // a restore flash. This is a client-only SPA, so we read synchronously during
  // render: the grid mounts immediately with its state and apiRef is ready for
  // consumer effects. Revisit if this app ever adopts SSR/prerendering.
  const initialState = ((): GridInitialStateFromApi | undefined => {
    // No key: caching disabled.
    if (!key) return undefined;

    // localStorage access can throw (private mode / blocked storage); fall back
    // to an empty state so a render-time read never crashes the grid.
    try {
      const stateFromLocalStorage = localStorage.getItem(key);
      if (!stateFromLocalStorage) return {} as GridInitialStateFromApi;
      return JSON.parse(stateFromLocalStorage) as GridInitialStateFromApi;
    } catch {
      return {} as GridInitialStateFromApi;
    }
  })();

  useEffect(() => {
    if (!key) return;

    const saveSnapshot = () => {
      if (!apiRef.current?.exportState) return;
      // Best-effort: storage can throw (quota / blocked) and this runs during
      // beforeunload/cleanup, so swallow failures rather than surface them.
      try {
        const currentState = apiRef.current.exportState();
        localStorage.setItem(key, JSON.stringify(currentState));
      } catch {
        // ignore
      }
    };

    // handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [key, apiRef]);

  return initialState;
};

export { useSaveGridSnapshot };
